import React from 'react'
import Component from '../../components/AscComponent'
import {FormControl, Row, Col, InputGroup, Button} from 'react-bootstrap'
import LoadingButton from '../Elements/Button/LoadingButton'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faUser, faLock, faKey} from "@fortawesome/free-solid-svg-icons";
import './SignIn.css'

// 認証コードの桁数（サーバ側 Mfa.js の生成桁数と合わせる）
const MFA_CODE_LENGTH = 6;

export default class SignIn extends Component {
    constructor(props) {
        super(props)
        this.state = {
            isLoading: false,
            userId: '',
            password: '',
            // Issue #443: メールOTPによる多要素認証（同一画面のステップ切替）
            mfaStep: false,
            mfaCode: '',
            maskedMail: '',
            expireSeconds: 0,
            remainingAttempts: 0,
            resendCooldownSeconds: 0,
            resendRemaining: 0,
            isVerifying: false,
            isResending: false,
        }
        // 有効期限・再送クールダウンのカウントダウン用
        this.mfaCountdownTimer = null;
    }

    validationForm = () => {
        return this.state.userId.length > 0 && this.state.password.length > 0;
    }

    handleSubmit = async event => {
        event.preventDefault();
        this.setState({isLoading: true});
        let {userId, password} = this.state;
        try {
            let response = await this.ascAxios('post', '/Sign/login', {
                userId,
                password
            });
            if (response.status === 200 && response.data && response.data.mfa_required) {
                // MFA有効ユーザーはセッション未発行。コード入力ステップへ切り替える
                this.startMfaStep(response.data);
            } else if (response.status === 200) {
                this.props.userHasAuthenticated(true, response.data);
                this.props.historyPush('/');
            } else {
                alert(this.props.langText.Message.SignIn_SignInFailed);
                this.setState({isLoading: false });
            }
        } catch (err) {
            console.error(err)
            let errorCode = err.response && err.response.data && err.response.data.code;
            if (errorCode && (errorCode.indexOf('Mfa_') === 0 || errorCode === 'SignIn_TooManyAttempts')) {
                // MFAのメール送信失敗・レート制限は原因が分かるようサーバのコードをそのまま表示する
                this.showErrorObjectMessage(err, 'SignIn_SignInFailed');
            } else if (err.response && err.response.data.code !== 'UserNotConfirmedException'){
                this.showErrorObjectMessage('SignIn_SignInFailed');
            } else {
                this.showErrorObjectMessage(err,'SignIn_SignInFailed');
            }
            this.setState({isLoading: false});
        }
    }

    /**
     * コード入力ステップの開始
     * @param {object} data /Sign/login のMFA応答
     */
    startMfaStep = data => {
        this.setState({
            isLoading: false,
            mfaStep: true,
            mfaCode: '',
            maskedMail: data.masked_mail || '',
            expireSeconds: data.expires_in || 0,
            remainingAttempts: data.max_attempts || 0,
            // 初回送信直後のためクールダウン中から開始する
            resendCooldownSeconds: data.resend_cooldown || 0,
            resendRemaining: data.max_resend !== undefined ? data.max_resend : 0,
        });
        this.startMfaCountdown();
    }

    /**
     * コード入力ステップの終了（ログイン画面へ戻す）
     * @param {string} messageCode 表示するメッセージコード。未指定なら何も表示しない
     */
    endMfaStep = messageCode => {
        this.stopMfaCountdown();
        this.setState({
            mfaStep: false,
            mfaCode: '',
            password: '',
            maskedMail: '',
            expireSeconds: 0,
            remainingAttempts: 0,
            resendCooldownSeconds: 0,
            resendRemaining: 0,
            isLoading: false,
            isVerifying: false,
            isResending: false,
        });
        if (messageCode) {
            this.showErrorObjectMessage(messageCode);
        }
    }

    startMfaCountdown = () => {
        this.stopMfaCountdown();
        this.mfaCountdownTimer = setInterval(() => {
            this.setState(prevState => ({
                expireSeconds: prevState.expireSeconds > 0 ? prevState.expireSeconds - 1 : 0,
                resendCooldownSeconds: prevState.resendCooldownSeconds > 0
                    ? prevState.resendCooldownSeconds - 1
                    : 0,
            }));
        }, 1000);
    }

    stopMfaCountdown = () => {
        if (this.mfaCountdownTimer) {
            clearInterval(this.mfaCountdownTimer);
            this.mfaCountdownTimer = null;
        }
    }

    /**
     * 残り時間表示（mm:ss）
     */
    getRemainingTimeText = seconds => {
        let minutes = Math.floor(seconds / 60);
        let sec = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    }

    // 数字以外は入力させない（貼り付け対策も兼ねて置換する）
    // 6桁そろった時点で自動的に認証する。メールからコピーして貼るだけで進むようにし、
    // 「入力したのにボタンを押していない」で止まる状態を無くす。
    onMfaCodeChange = event => {
        let value = (event && event.target && event.target.value) ? event.target.value : '';
        let code = value.replace(/[^0-9]/g, '').slice(0, MFA_CODE_LENGTH);
        this.setState({mfaCode: code}, () => {
            if (code.length === MFA_CODE_LENGTH
                && !this.state.isVerifying
                && this.state.expireSeconds > 0) {
                this.handleVerifyMfa();
            }
        });
    }

    validationMfaForm = () => {
        return this.state.mfaCode.length === MFA_CODE_LENGTH && this.state.expireSeconds > 0;
    }

    // 自動送信のときは event が渡らない
    handleVerifyMfa = async event => {
        if (event) event.preventDefault();
        if (this.state.isVerifying) return;
        this.setState({isVerifying: true});
        try {
            let response = await this.ascAxios('post', '/Sign/verifyMfa', {
                code: this.state.mfaCode
            });
            if (response.status === 200) {
                this.stopMfaCountdown();
                this.props.userHasAuthenticated(true, response.data);
                this.props.historyPush('/');
            }
        } catch (err) {
            console.error(err);
            let data = (err.response && err.response.data) || {};
            switch (data.code) {
                case 'Mfa_CodeExpired':
                case 'Mfa_AttemptsExceeded':
                case 'Mfa_SessionExpired':
                    // やり直しが必要なためログイン画面へ戻す
                    this.endMfaStep(data.code);
                    return;
                case 'Mfa_CodeInvalid':
                    this.setState({
                        mfaCode: '',
                        remainingAttempts: data.remaining_attempts !== undefined
                            ? data.remaining_attempts
                            : 0,
                        isVerifying: false
                    });
                    alert(this.getErrorString({
                        code: 'Mfa_CodeInvalidRemaining',
                        args: [data.remaining_attempts !== undefined ? data.remaining_attempts : 0]
                    }));
                    return;
                default:
                    this.showErrorObjectMessage(err, 'Mfa_VerifyFailed');
                    this.setState({isVerifying: false});
            }
        }
    }

    handleResendMfaCode = async () => {
        this.setState({isResending: true});
        try {
            let response = await this.ascAxios('post', '/Sign/resendMfaCode');
            this.setState({
                mfaCode: '',
                maskedMail: response.data.masked_mail || this.state.maskedMail,
                expireSeconds: response.data.expires_in || 0,
                // 再送直後もクールダウンが始まる。サーバの値を使い、画面側に秒数を持たない
                resendCooldownSeconds: response.data.resend_cooldown || 0,
                // 再送してもサーバ側の試行回数は上限へ戻らない（pending 単位で通算される）。
                // サーバが計算した残り回数を使い、返って来ない場合は現在の表示を据え置く
                remainingAttempts: response.data.remaining_attempts !== undefined
                    ? response.data.remaining_attempts
                    : this.state.remainingAttempts,
                resendRemaining: response.data.resend_remaining !== undefined
                    ? response.data.resend_remaining
                    : 0,
                isResending: false
            });
            alert(this.props.langText.Message.Mfa_CodeResent);
        } catch (err) {
            console.error(err);
            let data = (err.response && err.response.data) || {};
            switch (data.code) {
                case 'Mfa_SessionExpired':
                case 'Mfa_ResendLimitExceeded':
                    this.endMfaStep(data.code);
                    return;
                case 'Mfa_ResendCooldown':
                    this.setState({
                        resendCooldownSeconds: data.retry_after !== undefined ? data.retry_after : 0,
                        isResending: false
                    });
                    this.showErrorObjectMessage(err, 'Mfa_ResendFailed');
                    return;
                default:
                    this.showErrorObjectMessage(err, 'Mfa_ResendFailed');
                    this.setState({isResending: false});
            }
        }
    }

    componentWillUnmount() {
        this.stopMfaCountdown();
        super.componentWillUnmount();
    }

    getMfaForm = () => {
        let {
            mfaCode,
            maskedMail,
            expireSeconds,
            remainingAttempts,
            resendCooldownSeconds,
            resendRemaining,
            isVerifying,
            isResending
        } = this.state;
        let langBody = this.props.langText.Body;

        return (
            <form className='form-block mfa-form' onSubmit={this.handleVerifyMfa}>
                <div className='mfa-heading'>
                    <FontAwesomeIcon className='mfa-heading-icon' icon={faKey}/>
                    <span className='mfa-title'>{langBody.MfaTitle}</span>
                </div>
                <div className='mfa-description'>
                    {this.sprintf(langBody.MfaDescription, maskedMail)}
                </div>

                {/* コードは6桁の数字だけなので、アイコン付きの細い入力欄ではなく
                    桁が数えられる大きな入力欄にする。期限切れの間は入力させない。 */}
                <label className='mfa-code-label' htmlFor='mfaCode'>
                    {langBody.MfaCodePlaceholder}
                </label>
                <FormControl
                    id = 'mfaCode'
                    className = 'mfa-code-input'
                    autoFocus
                    type = 'text'
                    inputMode = 'numeric'
                    autoComplete = 'one-time-code'
                    maxLength = {MFA_CODE_LENGTH}
                    value = {mfaCode}
                    onChange = {this.onMfaCodeChange}
                    disabled = {isVerifying || expireSeconds <= 0}
                />

                {/* 有効期限と残り回数は1行に収める。期限切れは色を変えて目立たせる。 */}
                {expireSeconds > 0 ? (
                    <div className='mfa-meta'>
                        <span className='mfa-meta-item'>
                            {this.sprintf(langBody.MfaRemainingTime, this.getRemainingTimeText(expireSeconds))}
                        </span>
                        <span className='mfa-meta-item'>
                            {this.sprintf(langBody.MfaRemainingAttempts, remainingAttempts)}
                        </span>
                    </div>
                ) : (
                    <div className='mfa-meta mfa-meta-expired'>{langBody.MfaExpired}</div>
                )}

                <LoadingButton
                    className = 'signIn-button'
                    disabled = {!this.validationMfaForm()}
                    type = 'submit'
                    isLoading = {isVerifying}
                    text = {langBody.MfaVerify}
                    loadingText = {langBody.MfaVerifying}
                />

                {/* 再送はコードが届かなかった人だけが使う導線。認証ボタンより弱い見た目にする。
                    期限切れのときだけ強調して、次に押すべきボタンが分かるようにする。 */}
                <Button
                    className = {expireSeconds > 0 ? 'mfa-resend-button' : 'mfa-resend-button mfa-resend-primary'}
                    variant = 'outline-secondary'
                    disabled = {isResending || resendCooldownSeconds > 0 || resendRemaining <= 0}
                    onClick = {this.handleResendMfaCode}
                >
                    {resendCooldownSeconds > 0
                        ? this.sprintf(langBody.MfaResendWait, resendCooldownSeconds)
                        : langBody.MfaResend}
                </Button>
                <div className='mfa-meta mfa-meta-center'>
                    {this.sprintf(langBody.MfaResendRemaining, resendRemaining)}
                </div>
                <Button
                    className = 'mfa-back-button'
                    variant = 'link'
                    onClick = {() => this.endMfaStep()}
                >
                    {langBody.MfaBackToLogin}
                </Button>
            </form>
        );
    }

    getSignInForm = () => {
        return (
            <form className='form-block' onSubmit={this.handleSubmit}>
                <InputGroup>
                    <InputGroup.Text>
                        <FontAwesomeIcon icon={faUser}/>
                    </InputGroup.Text>
                    <FormControl
                        autoFocus
                        type = 'text'
                        value = {this.state.userId}
                        onChange = {e => this.onTextChange(e, 'userId')}
                        placeholder = {this.props.langText.Body.UserIdLogIn}
                    />
                </InputGroup>
                <InputGroup>
                    <InputGroup.Text>
                        <FontAwesomeIcon icon={faLock}/>
                    </InputGroup.Text>
                    <FormControl
                        value = {this.state.password}
                        onChange = {e => this.onTextChange(e, 'password')}
                        type = 'password'
                        placeholder = {this.props.langText.Body.Password}
                    />
                </InputGroup>
                <LoadingButton
                    className = 'signIn-button'
                    disabled = {!this.validationForm()}
                    type = 'submit'
                    isLoading = {this.state.isLoading}
                    text = {this.props.langText.Body.LogIn}
                    loadingText = {this.props.langText.Body.LogGingIn}
                />
            </form>
        );
    }

    render() {
        return (
        <Row>
            <Col lg={5} sm={5}>
            <div className="signIn-logo-string"><img src={this.props.systemParameters.LOGO} className="img-logo" alt={this.props.systemParameters.SYSTEMTITLE}></img></div>
            </Col>
            <Col lg={7} sm={7} className='signIn-col'>
                <div className='signIn'>
                    {this.state.mfaStep ? this.getMfaForm() : this.getSignInForm()}
                </div>
            </Col>
        </Row>
        )
    }
}
